/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  ButtonBase,
  Typography,
  TextField,
  Tooltip,
  CircularProgress,
  Button,
  LinearProgress,
  Stack,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Iconify from '../Iconify';
import DentallyAvailabilityResults from './DentallyAvailabilityResults';
import { fetchDentallyAvailability, fetchDentallyZones } from '../../utils/dentallyApi';
import { availPractitionerName, parseDentallyDate } from '../../utils/dentallyUtils';
import { bookingStepLabelStyle } from '../../styles/DentallyBookingStyle';
import { useSelector } from '../../redux/store';
import { selectAssignableUsers } from '../../utils/assignableUsers';
import { selectPracticeUsersForPractice } from '../../redux/slices/practiceUser';
import { RATE_LIMIT_REQUESTS, reserveDentallyAvailabilityRateSlot } from './dentallyAvailabilityRateBudget';

// Backend contract for /dentally/appointments/availability/ — mirrored here so the
// UI never builds a request the API is guaranteed to reject
// (apps/dentally/serializers.py:39-42, :447-476 and config/settings.py:436):
//
//   start_time  MUST be strictly > server now   → SEARCH_START_LEAD_MS
//   window      MUST be <= 7 days               → MAX_WINDOW_DAYS
//   practitioner_ids  1..2 per request          → MAX_PRACTITIONERS_PER_REQUEST
//   rate limit  20 requests / minute / practice → shared practice budget
//
// Previously the UI sent start_time = new Date() ("now"), which the serializer
// rejects because it is <= timezone.now() by the time Django evaluates it — so
// every preset search 400'd before Dentally was ever contacted, and the error was
// swallowed into an empty "No slots found".
const SEARCH_START_LEAD_MS = 60 * 1000;
const MAX_WINDOW_DAYS = 7;
const MAX_PRACTITIONERS_PER_REQUEST = 2;

// Searching everyone is allowed: instead of refusing above 8 practitioners, the
// shared practice budget paces every actual attempt across searches and component
// instances. Slots stream in as each batch lands and the search remains stoppable.
const MAX_COMPONENT_RETRY_AFTER_SECONDS = 30;

// "Next 14 Days" was removed: the backend caps the window at 7 days, so that
// preset could never return anything. Keeping it would mean either lying about
// the range searched or a guaranteed validation error.
const TIMEFRAME_PRESETS = [
  { key: '1day', label: 'Next 1 Day', days: 1 },
  { key: '2days', label: 'Next 2 Days', days: 2 },
  { key: '7days', label: 'Next 7 Days', days: MAX_WINDOW_DAYS },
  { key: 'custom', label: 'Custom', days: null },
];

// Split practitioners into request-sized batches. One request per practitioner
// (the previous behaviour) meant 13 active practitioners => 13 calls against a
// 4/min limit, so most were throttled and silently dropped.
const chunk = (items, size) => {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

// Turn a failed availability response into something worth showing the user.
const describeAvailabilityError = (res) => {
  if (!res) return 'Could not reach the diary. Check your connection and try again.';
  if (res.status === 429) {
    const retryAfter = Number(res.headers?.['retry-after'] ?? res.headers?.['Retry-After']);
    const wait = Number.isFinite(retryAfter) && retryAfter > 0 ? ` Try again in ${retryAfter}s.` : ' Try again shortly.';
    // Name the two things the old wording left the operator to guess: the
    // allowance is the whole practice's, not theirs, so a colleague searching
    // can exhaust it; and whatever already loaded is still good. Reported from
    // a demo where this read as "everything failed".
    return `The diary allows a limited number of lookups a minute, shared by everyone at this practice.${wait} Slots already found are still shown.`;
  }
  const { data } = res;
  if (data && typeof data === 'object') {
    if (typeof data.detail === 'string') return data.detail;
    // DRF field errors, e.g. { start_time: ["Availability search must start in the future."] }
    const firstField = Object.keys(data)[0];
    const value = firstField ? data[firstField] : null;
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    if (typeof value === 'string') return value;
  }
  return 'Dentally could not return availability for this search.';
};

const DURATION_OPTIONS = [10, 15, 20, 30, 45, 60, 75, 90, 110, 120];

// Split a large availability window into discrete time slots of the requested duration.
// Uses parseDentallyDate to handle DD-MM-YYYY format from the Dentally API.
const splitSlotsByDuration = (slots, durationMinutes) => {
  if (!durationMinutes || durationMinutes <= 0) return slots;
  const now = Date.now();
  const result = [];
  slots.forEach((slot) => {
    const startDate = parseDentallyDate(slot.start_time);
    const endDate = parseDentallyDate(slot.finish_time);
    if (!startDate || !endDate) return; // skip unparseable slots
    const start = startDate.getTime();
    const end = endDate.getTime();
    if (end <= now) return; // skip past slots entirely
    const windowMinutes = (end - start) / 60000;
    if (windowMinutes <= durationMinutes) {
      // Normalize to ISO so downstream code always sees ISO format
      result.push({ ...slot, start_time: startDate.toISOString(), finish_time: endDate.toISOString() });
      return;
    }
    const step = durationMinutes * 60000;
    let cursor = Math.max(start, now); // skip sub-slots in the past
    // Align cursor to the next slot boundary from the original start
    if (cursor > start) {
      const elapsed = cursor - start;
      cursor = start + Math.ceil(elapsed / step) * step;
    }
    while (cursor + step <= end) {
      result.push({
        ...slot,
        start_time: new Date(cursor).toISOString(),
        finish_time: new Date(cursor + step).toISOString(),
      });
      cursor += step;
    }
  });
  return result;
};

/**
 * Inline (non-drawer) availability slot picker for use as a wizard step.
 * Renders the practitioner selector, timeframe/duration presets, and slot grid
 * without any Drawer chrome.
 *
 * Props:
 *   practiceId  — required
 *   selectedSlot — currently selected slot (controlled, from parent state)
 *   onSelect    — called with slot object when slot is selected, or null to clear
 */
export default function DentallyAvailabilityStep({
  practiceId,
  selectedSlot = null,
  onSelect,
  assignToState = [],
  onAssignToChange = null,
  availSearchState = null,
  onAvailSearchStateChange = null,
  hideUserSelection = false,
  hideAssigneeSelection = false,
  allowManualSelection = true,
  compact = false,
}) {
  const theme = useTheme(); // eslint-disable-line no-unused-vars

  const {
    practitioners: storedPractitioners,
    isLoading: practLoading,
    practiceId: practitionerPracticeId,
  } = useSelector(
    (state) => state.dentallyPractitioners
  );
  const scopedPracticeUsers = useSelector((state) => selectPracticeUsersForPractice(state, practiceId));
  const { practice: practiceData } = useSelector((state) => state.practice);
  const practitionerStoreMatches = (
    practiceId != null
    && practitionerPracticeId != null
    && String(practiceId) === String(practitionerPracticeId)
  );
  const allPractitioners = practitionerStoreMatches ? storedPractitioners : [];

  // Same people the rest of the CRM offers as assignees. This used to exclude
  // only ADMIN/SUPPORT, which kept the PRACTITIONER role — and Dentally
  // practitioners are synced onto PracticeUser rows by email, so this list
  // showed clinicians nobody can assign a lead to anywhere else.
  const assignableUsers = useMemo(
    () => selectAssignableUsers(scopedPracticeUsers),
    [scopedPracticeUsers]
  );

  const activePractitioners = useMemo(() => {
    const seen = new Set();
    return (allPractitioners || []).filter((p) => {
      if (p.active === false || seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, [allPractitioners]);

  // Use lifted state from parent if provided, otherwise fall back to local state
  const [localPractitioners, setLocalPractitioners] = useState([]);
  const selectedPractitioners = availSearchState?.selectedPractitioners ?? localPractitioners;
  const setSelectedPractitioners = useCallback(
    (val) => {
      const next = typeof val === 'function' ? val(selectedPractitioners) : val;
      setLocalPractitioners(next);
      onAvailSearchStateChange?.((prev) => ({ ...prev, selectedPractitioners: next }));
    },
    [selectedPractitioners, onAvailSearchStateChange]
  );

  const allSelected = activePractitioners.length > 0 && selectedPractitioners.length === activePractitioners.length;

  const [practSearch, setPractSearch] = useState('');
  const [showAllPract, setShowAllPract] = useState(false);
  const MAX_VISIBLE_PRACTITIONERS = 6;
  // "Assigned to" listed every assignable user with no search or collapse — the same
  // avatar wall the Booked-with row above already solved.
  const [assignSearch, setAssignSearch] = useState('');
  const [showAllAssign, setShowAllAssign] = useState(false);

  const [localTimeframe, setLocalTimeframe] = useState('7days');
  const timeframePreset = availSearchState?.timeframePreset ?? localTimeframe;
  const setTimeframePreset = useCallback(
    (val) => {
      setLocalTimeframe(val);
      onAvailSearchStateChange?.((prev) => ({ ...prev, timeframePreset: val }));
    },
    [onAvailSearchStateChange]
  );

  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  const [localDuration, setLocalDuration] = useState(30);
  const duration = availSearchState?.duration ?? localDuration;
  const setDuration = useCallback(
    (val) => {
      setLocalDuration(val);
      onAvailSearchStateChange?.((prev) => ({ ...prev, duration: val }));
    },
    [onAvailSearchStateChange]
  );

  const [localResults, setLocalResults] = useState([]);
  const results = availSearchState?.results ?? localResults;
  const setResults = useCallback(
    (val) => {
      const next = typeof val === 'function' ? val(results) : val;
      setLocalResults(next);
      onAvailSearchStateChange?.((prev) => ({ ...prev, results: next }));
    },
    [results, onAvailSearchStateChange]
  );

  const [isSearching, setIsSearching] = useState(false);
  const [searchProgress, setSearchProgress] = useState({ done: 0, total: 0 });
  // Why the last search returned nothing, when the reason was a rejection rather
  // than an empty diary. Previously every failure collapsed into "No slots found".
  const [searchError, setSearchError] = useState(null);
  // Advisory zones for the searched window. Never gates slots: a provider
  // failure leaves this empty and the picker renders exactly as before.
  const [zones, setZones] = useState([]);
  // Transient status while a paced search is in flight ("pausing 42s", "retrying")
  // — informational, so kept separate from searchError, which means failure.
  const [searchNotice, setSearchNotice] = useState(null);
  const [searchOutcome, setSearchOutcome] = useState(null);
  const mountedRef = useRef(true);
  const activePracticeRef = useRef(String(practiceId));
  const previousPracticeRef = useRef(String(practiceId));
  const requestGenerationRef = useRef(0);
  const activeSearchControllerRef = useRef(null);

  const [localHasSearched, setLocalHasSearched] = useState(false);
  const hasSearched = availSearchState?.hasSearched ?? localHasSearched;
  const setHasSearched = useCallback(
    (val) => {
      setLocalHasSearched(val);
      onAvailSearchStateChange?.((prev) => ({ ...prev, hasSearched: val }));
    },
    [onAvailSearchStateChange]
  );

  useEffect(() => {
    const nextPractice = String(practiceId);
    const practiceChanged = previousPracticeRef.current !== nextPractice;
    activeSearchControllerRef.current?.abort();
    activeSearchControllerRef.current = null;
    activePracticeRef.current = String(practiceId);
    previousPracticeRef.current = nextPractice;
    requestGenerationRef.current += 1;
    setLocalResults([]);
    setLocalHasSearched(false);
    setIsSearching(false);
    setSearchError(null);
    setZones([]);
    setSearchNotice(null);
    setSearchOutcome(null);
    setSearchProgress({ done: 0, total: 0 });
    if (practiceChanged) setLocalPractitioners([]);
    onAvailSearchStateChange?.((prev) => ({
      ...prev,
      ...(practiceChanged && { selectedPractitioners: [] }),
      results: [],
      hasSearched: false,
    }));
    onSelect(null);
  }, [practiceId]);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            requestGenerationRef.current += 1;
            activeSearchControllerRef.current?.abort();
            activeSearchControllerRef.current = null;
        };
    }, []);

  // Build a window the backend will accept: start strictly in the future, and no
  // more than MAX_WINDOW_DAYS wide. Returns { start, end, error } — error is a
  // user-facing string when the requested range can't be honoured at all.
  const getTimeRange = useCallback(() => {
    const nowMs = Date.now();
    const earliestStartMs = nowMs + SEARCH_START_LEAD_MS;
    const maxWindowMs = MAX_WINDOW_DAYS * 24 * 60 * 60 * 1000;

    if (timeframePreset === 'custom') {
      if (!customStartDate || !customEndDate) {
        return { error: 'Choose a start and end date for the custom range.' };
      }
      const startMs = Math.max(customStartDate.getTime(), earliestStartMs);
      const endMs = customEndDate.getTime();
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
        return { error: 'That date range is not valid.' };
      }
      if (endMs <= startMs) {
        return { error: 'The end date must be after the start date.' };
      }
      if (endMs - startMs > maxWindowMs) {
        return { error: `Availability can only be searched ${MAX_WINDOW_DAYS} days at a time. Shorten the range.` };
      }
      return { start: new Date(startMs).toISOString(), end: new Date(endMs).toISOString() };
    }

    const preset = TIMEFRAME_PRESETS.find((p) => p.key === timeframePreset);
    const days = preset?.days || MAX_WINDOW_DAYS;
    // End is measured from now, start from now + lead, so the window is always a
    // shade under `days` and can never trip the >7-day check on the boundary.
    return {
      start: new Date(earliestStartMs).toISOString(),
      end: new Date(nowMs + days * 24 * 60 * 60 * 1000).toISOString(),
    };
  }, [timeframePreset, customStartDate, customEndDate]);

  const handleSearch = useCallback(async () => {
    const activeIds = new Set(activePractitioners.map((practitioner) => String(practitioner.id)));
    const scopedPractitioners = selectedPractitioners.filter(
      (practitioner) => activeIds.has(String(practitioner.id))
    );
    if (!practiceId || !practitionerStoreMatches || scopedPractitioners.length === 0) return;
    const effectiveDuration = typeof duration === 'number' && duration > 0 ? duration : 30;

    const { start, end, error: rangeError } = getTimeRange();
    if (rangeError) {
      setSearchError(rangeError);
      setResults([]);
      setHasSearched(true);
      return;
    }
    setSearchError(null);

    const capturedPractice = String(practiceId);
    const requestGeneration = requestGenerationRef.current + 1;
    requestGenerationRef.current = requestGeneration;
    activeSearchControllerRef.current?.abort();
    const controller = new AbortController();
    activeSearchControllerRef.current = controller;
    const isCurrentRequest = () => (
      mountedRef.current
      && !controller.signal.aborted
      && requestGenerationRef.current === requestGeneration
      && activePracticeRef.current === capturedPractice
    );
    setIsSearching(true);
    setHasSearched(true);
    setResults([]);
    setSearchOutcome(null);
    onSelect(null); // clear any previously selected slot

    const nameMap = {};
    scopedPractitioners.forEach((p) => {
      nameMap[p.id] = availPractitionerName(p);
    });

    // Two practitioners per request (the backend maximum), not one each.
    const batches = chunk(scopedPractitioners, MAX_PRACTITIONERS_PER_REQUEST);
    setSearchProgress({ done: 0, total: batches.length });

    // Collected so a partial failure can be reported instead of silently
    // shrinking the result set.
    const failures = [];
    const collected = [];
    const stampSlots = (slots, batch) => (Array.isArray(slots) ? slots : []).map((s) => {
      // The API stamps practitioner_id on every slot; fall back to the batch
      // member only when the batch was a single practitioner.
      const ownerId = s.practitioner_id ?? (batch.length === 1 ? batch[0].id : null);
      return {
        ...s,
        _queried_practitioner_id: ownerId,
        _queried_practitioner_name: nameMap[ownerId] || '',
      };
    });

    // Publish what we have so far, so slots appear as each batch lands rather
    // than only after the slowest one.
    const publish = () => {
      const sliced = splitSlotsByDuration(collected, effectiveDuration);
      sliced.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      setResults(sliced);
    };

    // Sleep that gives up promptly if the search was superseded or cancelled.
    //
    // Deadline-based on purpose. Counting fixed-size steps and assuming each took
    // exactly that long is wrong in a background tab: browsers clamp setTimeout to
    // ~1s (or slower) when the page is not focused, so a 250ms × 236 loop waited
    // several minutes instead of 59s and the search appeared to hang. Comparing
    // against wall-clock can't drift that way.
    const waitOrAbort = async (ms, onTick) => {
      const deadline = Date.now() + ms;
      while (Date.now() < deadline) {
        if (!isCurrentRequest()) return false;
        onTick?.(Math.ceil((deadline - Date.now()) / 1000));
        const remaining = deadline - Date.now();
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, Math.min(1000, Math.max(remaining, 0))));
      }
      return isCurrentRequest();
    };

    // Reserve every actual attempt against the shared practice budget. The
    // history lives outside this component so restart/remount cannot reset it.
    const awaitRateSlot = async () => {
      while (isCurrentRequest()) {
        const reservation = reserveDentallyAvailabilityRateSlot(capturedPractice);
        if (reservation.reserved) return true;
        const tick = (secondsLeft) => setSearchNotice(
          `Checking the rest — the diary allows ${RATE_LIMIT_REQUESTS} lookups a minute, so this pauses for ${secondsLeft}s.`
        );
        tick(Math.ceil(reservation.waitMs / 1000));
        // eslint-disable-next-line no-await-in-loop
        if (!(await waitOrAbort(reservation.waitMs, tick))) return false;
      }
      return false;
    };

    const requestBatch = async (batch) => {
      if (!(await awaitRateSlot()) || !isCurrentRequest()) return null;
      try {
        return await fetchDentallyAvailability(practiceId, {
          startTime: start,
          finishTime: end,
          practitionerIds: batch.map((p) => p.id),
          duration: effectiveDuration,
          perPage: 50,
          page: 1,
        }, { signal: controller.signal });
      } catch {
        return null;
      }
    };

    try {
      for (let i = 0; i < batches.length; i += 1) {
        const batch = batches[i];
        // eslint-disable-next-line no-await-in-loop
        let res = await requestBatch(batch);
        if (!isCurrentRequest()) return;

        // A 429 means our pacing was still too optimistic (other users share the
        // backend budget). Only one bounded component-owned retry is allowed;
        // that retry must reserve another slot before it reaches the network.
        if (res && res.status === 429) {
          const retryAfter = Number(
            res.retryAfter?.seconds
            ?? res.headers?.['retry-after']
            ?? res.headers?.['Retry-After']
          );
          const canRetry = (
            Number.isSafeInteger(retryAfter)
            && retryAfter >= 0
            && retryAfter <= MAX_COMPONENT_RETRY_AFTER_SECONDS
          );
          if (canRetry) {
            const backoffMs = retryAfter * 1000;
            setSearchNotice(`Diary busy — retrying in ${retryAfter}s.`);
            // eslint-disable-next-line no-await-in-loop
            if (!(await waitOrAbort(backoffMs))) return;
            // eslint-disable-next-line no-await-in-loop
            res = await requestBatch(batch);
            if (!isCurrentRequest()) return;
          }
        }

        if (res && (res.ok || res.status === 200)) {
          collected.push(...stampSlots(res.data?.availability || [], batch));
          setSearchNotice(null);
        } else {
          failures.push(res ? describeAvailabilityError(res) : 'Could not reach the diary. Check your connection and try again.');
        }

        setSearchProgress({ done: i + 1, total: batches.length });
        publish();
      }

      // Annotate the same window with zones. Deliberately AFTER the slots are
      // on screen and never awaited by them: zones say what the time is for,
      // and failing to answer that must not delay or block showing what is
      // free. Shares the availability throttle server-side, so this sits
      // inside the existing per-practice budget rather than beside it.
      // Reuse the very window this search ran on, rather than recomputing it.
      // getTimeRange() reads Date.now(), so calling it twice returned windows a
      // few milliseconds apart - and the band must describe the slots actually
      // on screen, not an almost-identical range. The original defect was worse
      // than a drift: it read `.finish` from a function that returns
      // { start, end }, and `new Date(undefined).toISOString()` throws, so the
      // request was never issued at all and nothing was logged.
      if (isCurrentRequest()) {
        fetchDentallyZones(capturedPractice, { after: start, before: end })
          .then((res) => {
            if (!isCurrentRequest()) return;
            setZones(res?.status === 200 && Array.isArray(res?.data?.zones) ? res.data.zones : []);
          })
          .catch(() => { if (isCurrentRequest()) setZones([]); });
      }

      if (!isCurrentRequest()) return;
      // Show the reason when nothing came back, and flag partial failures too.
      if (failures.length) {
        const unique = [...new Set(failures)];
        setSearchError(
          collected.length === 0
            ? unique[0]
            : `Some practitioners could not be checked: ${unique[0]}`
        );
      } else {
        setSearchError(null);
      }
    } catch {
      if (isCurrentRequest()) publish();
    } finally {
      // Guarded on generation only, NOT isCurrentRequest(): if this run was
      // orphaned mid-pace (practice switch, remount) the old guard left
      // isSearching stuck true forever, freezing the UI on "Searching 4/7…".
      // A newer search sets its own isSearching, so only clear when none took over.
      if (requestGenerationRef.current === requestGeneration) {
        setIsSearching(false);
        setSearchNotice(null);
        if (activeSearchControllerRef.current === controller) {
          activeSearchControllerRef.current = null;
        }
      }
    }
  }, [
    activePractitioners,
    duration,
    getTimeRange,
    onSelect,
    practiceId,
    practitionerStoreMatches,
    selectedPractitioners,
  ]);

  const cancelActiveSearch = useCallback(({ criteriaChanged = false } = {}) => {
    const hadActiveSearch = Boolean(activeSearchControllerRef.current);
    if (hadActiveSearch) {
      activeSearchControllerRef.current.abort();
      activeSearchControllerRef.current = null;
      requestGenerationRef.current += 1;
      setIsSearching(false);
      setSearchNotice(null);
    }
    if (criteriaChanged) {
      setResults([]);
      setHasSearched(false);
      setSearchError(null);
      onSelect(null);
      setSearchOutcome(hadActiveSearch ? {
        type: 'criteria-changed',
        done: searchProgress.done,
        total: searchProgress.total,
      } : null);
    } else if (hadActiveSearch) {
      setSearchOutcome({
        type: 'stopped',
        done: searchProgress.done,
        total: searchProgress.total,
      });
    }
  }, [onSelect, searchProgress.done, searchProgress.total]);

  const handleStopSearch = useCallback(() => {
    cancelActiveSearch();
  }, [cancelActiveSearch]);

  const changeSearchCriteria = useCallback((change) => {
    cancelActiveSearch({ criteriaChanged: true });
    change();
  }, [cancelActiveSearch]);

  const handleToggleSelectAll = () => {
    changeSearchCriteria(() => {
      if (allSelected) {
        setSelectedPractitioners([]);
      } else {
        setSelectedPractitioners([...activePractitioners]);
      }
    });
  };

  const canSearch =
    practitionerStoreMatches
    && selectedPractitioners.some((selected) => (
      activePractitioners.some((active) => String(active.id) === String(selected.id))
    ))
    && (timeframePreset !== 'custom' || (customStartDate && customEndDate));

  // DEV-only: auto-select "Dr Raj Gohil" practitioner + trigger search (Test Practice only)
  const isTestPractice = practiceData?.name?.toLowerCase().includes('test practice');
  const devAutoSearched = useRef(false);
  useEffect(() => {
    if (
      !import.meta.env.DEV ||
      !isTestPractice ||
      devAutoSearched.current ||
      selectedSlot ||
      practLoading ||
      !activePractitioners.length
    )
      return;
    const rajGohil = activePractitioners.find((p) => {
      const u = p.user || {};
      const fullName = [u.title, u.first_name, u.last_name].filter(Boolean).join(' ').toLowerCase();
      return fullName.includes('raj') && fullName.includes('gohil');
    });
    if (rajGohil) {
      setSelectedPractitioners([rajGohil]);
      devAutoSearched.current = true;
    }
  }, [activePractitioners, practLoading, isTestPractice]);

  // DEV-only: auto-trigger search once practitioner is selected
  useEffect(() => {
    if (
      !import.meta.env.DEV ||
      !isTestPractice ||
      !devAutoSearched.current ||
      selectedPractitioners.length === 0 ||
      isSearching ||
      hasSearched
    )
      return;
    handleSearch();
  }, [selectedPractitioners, isTestPractice]);

  // DEV-only: auto-select first available slot when results arrive
  useEffect(() => {
    if (!import.meta.env.DEV || !isTestPractice || !devAutoSearched.current || selectedSlot || results.length === 0)
      return;
    onSelect(results[0]);
  }, [results, isTestPractice]);

  const progressPercent = searchProgress.total > 0 ? Math.round((searchProgress.done / searchProgress.total) * 100) : 0;

  return (
    <Box>
      {/* ── 👥 TEAM SECTION ── */}
      {!hideUserSelection && (
        <Box sx={{ mb: 2.5 }}>
          {!compact && <Typography sx={bookingStepLabelStyle}>Team</Typography>}
          <Box
            sx={{
              ...(!compact && { background: '#f8f9fa', borderRadius: '12px', p: 2 }),
              display: 'flex',
              gap: compact ? 2 : 4,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: '#919EAB',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  fontWeight: 600,
                  mb: 0.75,
                }}
              >
                Booked with
              </Typography>
              {practLoading ? (
                <CircularProgress size={16} />
              ) : (
                <>
                  {/* Search — only when more than MAX_VISIBLE */}
                  {activePractitioners.length > MAX_VISIBLE_PRACTITIONERS && (
                    <TextField
                      size="small"
                      fullWidth
                      value={practSearch}
                      onChange={(e) => {
                        setPractSearch(e.target.value);
                        if (e.target.value) setShowAllPract(true);
                      }}
                      placeholder="Search practitioners…"
                      sx={{
                        mb: 0.75,
                        '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '12px', height: 28 },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' },
                      }}
                    />
                  )}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(() => {
                      const q = practSearch.toLowerCase();
                      const filtered = q
                        ? activePractitioners.filter((p) => availPractitionerName(p).toLowerCase().includes(q))
                        : activePractitioners;
                      const visible = showAllPract ? filtered : filtered.slice(0, MAX_VISIBLE_PRACTITIONERS);
                      const overflow = filtered.length - MAX_VISIBLE_PRACTITIONERS;
                      return (
                        <>
                          {visible.map((p) => {
                            const u = p.user || {};
                            const initials =
                              ((u.first_name?.[0] || '') + (u.last_name?.[0] || '')).toUpperCase() || '?';
                            const name = availPractitionerName(p);
                            const isSelected = selectedPractitioners.some((sp) => sp.id === p.id);
                            const colour = p.colour || '#078DEE';
                            return (
                              <Tooltip
                                key={p.id}
                                title={name}
                                arrow
                                placement="top"
                                componentsProps={{
                                  tooltip: { sx: { bgcolor: '#078DEE', fontSize: '11px', fontWeight: 600 } },
                                  arrow: { sx: { color: '#078DEE' } },
                                }}
                              >
                                <Box
                                  onClick={() => {
                                    changeSearchCriteria(() => {
                                      if (isSelected)
                                        setSelectedPractitioners((prev) => prev.filter((sp) => sp.id !== p.id));
                                      else setSelectedPractitioners((prev) => [...prev, p]);
                                    });
                                  }}
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    border: '2px solid',
                                    borderColor: isSelected ? '#00AB55' : 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: isSelected ? 'rgba(0,171,85,0.06)' : `${colour}14`,
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    color: isSelected ? '#00AB55' : colour,
                                    transition: 'all 150ms ease',
                                    '&:hover': { borderColor: colour },
                                  }}
                                >
                                  {initials}
                                </Box>
                              </Tooltip>
                            );
                          })}
                          {!showAllPract && overflow > 0 && !q && (
                            <Tooltip title={`Show ${overflow} more`} arrow placement="top">
                              <Box
                                onClick={() => setShowAllPract(true)}
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1.5px dashed',
                                  borderColor: '#C4CDD5',
                                  color: '#919EAB',
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  transition: 'all 150ms ease',
                                  '&:hover': { borderColor: '#078DEE', color: '#078DEE' },
                                }}
                              >
                                +{overflow}
                              </Box>
                            </Tooltip>
                          )}
                        </>
                      );
                    })()}
                    {activePractitioners.length > 0 && (
                      <Tooltip title={allSelected ? 'Deselect all' : 'Select all'} arrow placement="top">
                        <Box
                          onClick={handleToggleSelectAll}
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1.5px dashed',
                            borderColor: allSelected ? '#00AB55' : '#C4CDD5',
                            color: allSelected ? '#00AB55' : '#919EAB',
                            fontSize: '9px',
                            fontWeight: 700,
                            transition: 'all 150ms ease',
                            '&:hover': { borderColor: '#00AB55', color: '#00AB55' },
                          }}
                        >
                          {allSelected ? <Iconify icon="mdi:check-all" width={14} /> : 'ALL'}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </>
              )}
            </Box>
            {!hideAssigneeSelection && assignableUsers.length > 0 && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: '#919EAB',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    fontWeight: 600,
                    mb: 0.75,
                  }}
                >
                  Assigned to
                </Typography>
                {assignableUsers.length > MAX_VISIBLE_PRACTITIONERS && showAllAssign && (
                  <Box sx={{ mb: 0.75 }}>
                    <input
                      type="text"
                      value={assignSearch}
                      onChange={(e) => setAssignSearch(e.target.value)}
                      placeholder="Search people…"
                      aria-label="Search people to assign"
                      style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: '10px', padding: '6px 10px', fontSize: '12px', width: '100%', maxWidth: 200, outline: 'none' }}
                    />
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(() => {
                  const nameOf = (pu) => {
                    const uu = pu.user || {};
                    return [uu.first_name, uu.last_name].filter(Boolean).join(' ') || 'User';
                  };
                  const q = assignSearch.toLowerCase();
                  const picked = (pu) => (assignToState || []).some((a) => a.id === pu.id);
                  const filtered = q ? assignableUsers.filter((pu) => nameOf(pu).toLowerCase().includes(q)) : assignableUsers;
                  // Selected first so collapsing never hides an existing assignee.
                  const ordered = [...filtered].sort((a, b) => Number(picked(b)) - Number(picked(a)));
                  const visible = showAllAssign ? ordered : ordered.slice(0, MAX_VISIBLE_PRACTITIONERS);
                  const overflow = ordered.length - MAX_VISIBLE_PRACTITIONERS;
                  return (
                    <>
                  {visible.map((pu) => {
                    const u = pu.user || {};
                    const fullName = nameOf(pu);
                    const initials = ((u.first_name?.[0] || '') + (u.last_name?.[0] || '')).toUpperCase() || '?';
                    const isSelected = picked(pu);
                    return (
                      <Tooltip
                        key={pu.id}
                        title={fullName}
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: { sx: { bgcolor: '#855CF8', fontSize: '11px', fontWeight: 600 } },
                          arrow: { sx: { color: '#855CF8' } },
                        }}
                      >
                        {/* ButtonBase, not a clickable Box: this row had no keyboard
                            access, no aria-pressed and no focus ring. */}
                        <ButtonBase
                          component="button"
                          type="button"
                          aria-label={`${isSelected ? 'Deselect' : 'Select'} ${fullName} as assigned to`}
                          aria-pressed={isSelected}
                          onClick={() => {
                            if (!onAssignToChange) return;
                            if (isSelected) onAssignToChange((assignToState || []).filter((a) => a.id !== pu.id));
                            else onAssignToChange([...(assignToState || []), pu]);
                          }}
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            border: '2px solid',
                            borderColor: isSelected ? '#00AB55' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isSelected ? 'rgba(0,171,85,0.06)' : 'rgba(133,92,248,0.08)',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: isSelected ? '#00AB55' : '#855CF8',
                            transition: 'all 150ms ease',
                            '&:hover': { borderColor: '#855CF8' },
                            '&.Mui-focusVisible': { outline: '2px solid #00AB55', outlineOffset: '2px' },
                          }}
                        >
                          {initials}
                        </ButtonBase>
                      </Tooltip>
                    );
                  })}
                  {!showAllAssign && overflow > 0 && !q && (
                    <Tooltip title={`Show ${overflow} more`} arrow placement="top">
                      <ButtonBase
                        component="button"
                        type="button"
                        aria-label={`Show ${overflow} more people`}
                        onClick={() => setShowAllAssign(true)}
                        sx={{
                          width: 30, height: 30, borderRadius: '50%', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: '1.5px dashed', borderColor: 'rgba(0,0,0,0.16)',
                          color: '#6e6e73', fontSize: '10px', fontWeight: 700,
                          transition: 'all 150ms ease',
                          '&:hover': { borderColor: '#00AB55', color: '#00AB55' },
                          '&.Mui-focusVisible': { outline: '2px solid #00AB55', outlineOffset: '2px' },
                        }}
                      >
                        +{overflow}
                      </ButtonBase>
                    </Tooltip>
                  )}
                    </>
                  );
                  })()}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ── 📅 AVAILABILITY SECTION ── */}
      <Box sx={{ mb: 2 }}>
        {!compact && <Typography sx={bookingStepLabelStyle}>Availability</Typography>}
        <Box sx={{ ...(!compact && { background: '#f8f9fa', borderRadius: '12px', p: 2 }) }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: compact ? 1 : 2,
              mb: 1.5,
              flexDirection: compact ? 'column' : 'row',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: '#919EAB',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Timeframe
              </Typography>
              {/* eslint-disable no-nested-ternary */}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {TIMEFRAME_PRESETS.map(({ key, label }) => (
                  <Box
                    key={key}
                    onClick={() => changeSearchCriteria(() => setTimeframePreset(key))}
                    sx={{
                      px: compact ? 0.75 : 1.25,
                      py: compact ? 0.25 : 0.375,
                      borderRadius: compact ? '6px' : '14px',
                      fontSize: compact ? '10px' : '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: timeframePreset === key ? '#00AB55' : compact ? 'transparent' : '#e0e0e0',
                      color: timeframePreset === key ? '#00AB55' : '#919EAB',
                      background: timeframePreset === key ? 'rgba(0,171,85,0.06)' : compact ? '#f4f6f8' : 'transparent',
                      transition: 'all 150ms ease',
                      whiteSpace: 'nowrap',
                      '&:hover': { color: '#00AB55', background: 'rgba(0,171,85,0.06)' },
                    }}
                  >
                    {compact ? label.replace('Next ', '') : label}
                  </Box>
                ))}
              </Box>
              {/* eslint-enable no-nested-ternary */}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: '#919EAB',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Duration
              </Typography>
              <TextField
                select
                size="small"
                value={duration}
                onChange={(e) => changeSearchCriteria(() => setDuration(e.target.value))}
                sx={{
                  minWidth: compact ? 80 : 130,
                  ...(compact && {
                    '& .MuiOutlinedInput-root': { fontSize: '12px', height: 28, borderRadius: '8px' },
                    '& .MuiSelect-select': { py: '4px' },
                  }),
                }}
              >
                {DURATION_OPTIONS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {(() => {
                      if (compact) {
                        if (m < 60) return `${m}m`;
                        return `${Math.floor(m / 60)}h${m % 60 > 0 ? `${m % 60}` : ''}`;
                      }
                      if (m < 60) return `${m} min`;
                      if (m === 60) return '1 hour';
                      return `${Math.floor(m / 60)}h ${m % 60 > 0 ? `${m % 60}m` : ''}`.trim();
                    })()}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
          {timeframePreset === 'custom' && (
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              <DateTimePicker
                label="Start"
                value={customStartDate}
                onChange={(value) => changeSearchCriteria(() => setCustomStartDate(value))}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                minDateTime={new Date()}
              />
              <DateTimePicker
                label="End"
                value={customEndDate}
                onChange={(value) => changeSearchCriteria(() => setCustomEndDate(value))}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                minDateTime={customStartDate || new Date()}
              />
            </Stack>
          )}

          {/* Search button */}
          <LoadingButton
            fullWidth
            variant="contained"
            loading={isSearching}
            onClick={handleSearch}
            disabled={!canSearch}
            startIcon={<Iconify icon="mdi:magnify" width={compact ? 14 : 18} />}
            sx={{
              mb: 1,
              py: compact ? 0.5 : 1,
              fontWeight: 600,
              fontSize: compact ? '12px' : '0.875rem',
              borderRadius: compact ? '8px' : undefined,
            }}
          >
            {isSearching
              ? `Searching ${searchProgress.done}/${searchProgress.total}…`
              : 'Search Availability'}
          </LoadingButton>

          {isSearching && searchProgress.total > 1 && (
            <LinearProgress variant="determinate" value={progressPercent} sx={{ mb: 1 }} />
          )}

          {/* A large selection paces itself around the diary's per-minute budget, so
              it must be interruptible rather than leaving the user stuck watching it. */}
          {isSearching && (
            <Box sx={{ textAlign: 'center', mb: 1 }}>
              <Button
                size="small"
                onClick={handleStopSearch}
                sx={{ textTransform: 'none', color: 'text.secondary', fontSize: compact ? 11 : 12 }}
              >
                Stop searching — keep the slots found so far
              </Button>
            </Box>
          )}

          {/* Some wizard flows collect the manual time in their next step. Callers
              that require a concrete Dentally slot must not expose that sentinel. */}
          {allowManualSelection && (
            <Box sx={{ textAlign: 'center', mb: 1 }}>
              <Button
                size="small"
                onClick={() => onSelect({ _manual: true })}
                sx={{ textTransform: 'none', color: 'text.secondary', fontSize: compact ? 11 : 12 }}
              >
                Skip — Enter Appointment Time Manually
              </Button>
            </Box>
          )}

          {/* Pacing status while a large selection works through the per-minute
              budget — informational, so it stays visible during the search. */}
          {searchNotice && isSearching && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {searchNotice}
            </Alert>
          )}

          {/* Search failure — a rejected request must not read as an empty diary. */}
          {searchError && !isSearching && (
            <Alert severity={results.length > 0 ? 'warning' : 'error'} sx={{ mb: 1 }}>
              {searchError}
            </Alert>
          )}

          {searchOutcome && !isSearching && !searchError && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {searchOutcome.type === 'criteria-changed'
                ? 'Search stopped because the criteria changed. Search again to refresh the diary.'
                : `Search stopped after ${searchOutcome.done} of ${searchOutcome.total} checks. Slots found so far are kept.`}
            </Alert>
          )}

          {/* Results — suppressed entirely on a failed search with nothing to show,
              so the "No availability found / try a wider timeframe" empty state can't
              contradict the error above it (the timeframe was never the problem). */}
          {hasSearched && !(searchError && results.length === 0) && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontSize: compact ? 10 : undefined }}
              >
                {(() => {
                  if (searchOutcome?.type === 'stopped') {
                    return results.length > 0
                      ? `${results.length} slot(s) found before the search stopped`
                      : 'No complete availability result — the search was stopped early';
                  }
                  if (results.length > 0) {
                    // Mid-search the count is partial — don't imply it's the final tally.
                    return isSearching
                      ? `${results.length} slot(s) so far…`
                      : `${results.length} slot(s) found`;
                  }
                  // "No slots found" is an outcome, so only claim it once finished.
                  return isSearching ? 'Checking availability…' : 'No slots found';
                })()}
              </Typography>
              <DentallyAvailabilityResults
                results={results}
                onSelect={onSelect}
                practitioners={allPractitioners}
                selectedSlot={selectedSlot}
                loading={isSearching}
                compact={compact}
                zones={zones}
              />
            </>
          )}

          {/* Selected slot summary */}
          {selectedSlot && !selectedSlot._manual && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: 'rgba(0,171,85,0.08)',
                borderRadius: 1,
                border: '1px solid rgba(0,171,85,0.3)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify icon="mdi:check-circle" width={18} sx={{ color: 'success.main' }} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Slot selected
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(parseDentallyDate(selectedSlot.start_time) || new Date(selectedSlot.start_time)).toLocaleString(
                      'en-GB',
                      {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      }
                    )}
                    {selectedSlot._queried_practitioner_name && ` · ${selectedSlot._queried_practitioner_name}`}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Manual slot indicator */}
          {selectedSlot?._manual && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: 'rgba(255,171,0,0.08)',
                borderRadius: 1,
                border: '1px solid rgba(255,171,0,0.3)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify icon="mdi:pencil-outline" width={18} sx={{ color: 'warning.main' }} />
                <Typography variant="body2">
                  Manual booking — appointment time will be entered in the next step
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

DentallyAvailabilityStep.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  selectedSlot: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  assignToState: PropTypes.array,
  onAssignToChange: PropTypes.func,
  availSearchState: PropTypes.object,
  onAvailSearchStateChange: PropTypes.func,
  hideUserSelection: PropTypes.bool,
  hideAssigneeSelection: PropTypes.bool,
  allowManualSelection: PropTypes.bool,
  compact: PropTypes.bool,
};
